import React, { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Landing from '@/pages/Landing';
import Dashboard from '@/pages/Dashboard';
import Members from '@/pages/Members';
import Businesses from '@/pages/Businesses';
import Events from '@/pages/Events';
import Opportunities from '@/pages/Opportunities';
import Profile from '@/pages/Profile';
import Membership from '@/pages/Membership';
import PaymentSuccess from '@/pages/PaymentSuccess';
import PartnerPortal from '@/pages/PartnerPortal';
import BecomePartner from '@/pages/BecomePartner';
import AdminPanel from '@/pages/AdminPanel';
import ResetPassword from '@/pages/ResetPassword';
import AuthCallback from '@/pages/AuthCallback';
import AcceptInvitation from '@/pages/AcceptInvitation';
import ContactUs from '@/pages/ContactUs';
import PartnerWithUs from '@/pages/PartnerWithUs';
import Forum from '@/pages/Forum';
import EmployeeProfile from '@/pages/EmployeeProfile';
import VerifyEmail from '@/pages/VerifyEmail';
import Impressum from '@/pages/Impressum';
import Datenschutz from '@/pages/Datenschutz';
import UserGuide from '@/pages/UserGuide';
import AdminGuide from '@/pages/AdminGuide';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

// Component to handle home route with redirect logic
const HomeRoute = ({ user }) => {
  const location = useLocation();
  
  if (!user) {
    return <Landing />;
  }
  
  // Check if there's a redirect parameter
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirect');
  
  if (redirectTo) {
    return <Navigate to={`/${redirectTo}`} replace />;
  }
  
  return <Navigate to="/dashboard" replace />;
};

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/users/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Failed to fetch user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, [token]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-rose-50 via-white to-sage-50">
        <img src="/logo.png" alt="Her Enterprise Hub" className="h-32 w-auto mb-4 animate-pulse" />
        <div className="text-xl text-rose-600">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomeRoute user={user} />} />
            <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/" />} />
            <Route path="/members" element={<Members />} />
            <Route path="/businesses" element={<Businesses />} />
            <Route path="/events" element={<Events />} />
            <Route path="/opportunities" element={user ? <Opportunities /> : <Navigate to="/" />} />
            <Route path="/forum" element={<Forum />} />
            <Route path="/employee/profile" element={user ? <EmployeeProfile /> : <Navigate to="/" />} />
            <Route path="/profile/:id" element={user ? <Profile /> : <Navigate to="/" />} />
            <Route path="/membership" element={user ? <Membership /> : <Navigate to="/" />} />
            <Route path="/payment-success" element={user ? <PaymentSuccess /> : <Navigate to="/" />} />
            <Route path="/partner-portal" element={user ? <PartnerPortal /> : <Navigate to="/" />} />
            <Route path="/become-partner" element={<BecomePartner />} />
            <Route path="/admin" element={user ? <AdminPanel /> : <Navigate to="/" />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/partner" element={<PartnerWithUs />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/privacy" element={<Datenschutz />} />
            <Route path="/user-guide" element={<UserGuide />} />
            <Route path="/help" element={<UserGuide />} />
            <Route path="/admin/guide" element={<AdminGuide />} />
            <Route path="/admin-guide" element={<AdminGuide />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;